import { useEffect, useMemo, useState } from "react";
import { Check, ChevronsUpDown, MapPin, ArrowRight, Lock, BarChart3 } from "lucide-react";
import { toast } from "sonner";

import { api, formatApiError, computeFingerprint } from "../lib/api";
import { Button } from "./ui/button";
import { Popover, PopoverContent, PopoverTrigger } from "./ui/popover";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "./ui/command";

function formatLimitText(check) {
  if (!check) return null;
  const mode = check.mode;
  const days = Number(check.days) || 1;
  if (mode === "off") return null;
  if (mode === "lifetime") return "Tylko 1 głos na osobę";
  if (mode === "days") {
    if (days === 1) return "Możesz głosować raz dziennie";
    if (days === 2) return "Możesz głosować co 2 dni";
    return `Możesz głosować co ${days} dni`;
  }
  return null;
}

function formatRemaining(nextAllowedAt) {
  if (!nextAllowedAt) return null;
  const ms = new Date(nextAllowedAt).getTime() - Date.now();
  if (ms <= 0) return null;
  const days = Math.floor(ms / 86_400_000);
  const hours = Math.floor((ms % 86_400_000) / 3_600_000);
  const minutes = Math.floor((ms % 3_600_000) / 60_000);
  if (days >= 1) return `${days} d ${String(hours).padStart(2, "0")} h`;
  return `${String(hours).padStart(2, "0")}:${String(minutes).padStart(2, "0")}`;
}

function LockedNotice({ check, onShowResults }) {
  const [, force] = useState(0);
  useEffect(() => {
    if (!check?.next_allowed_at) return;
    const id = setInterval(() => force((n) => n + 1), 1000);
    return () => clearInterval(id);
  }, [check?.next_allowed_at]);

  const isLifetime = check?.mode === "lifetime";
  const remaining = formatRemaining(check?.next_allowed_at);

  return (
    <div data-testid="vote-locked" className="text-center py-2">
      <div className="inline-flex items-center justify-center w-14 h-14 rounded-full bg-brand-green/10 mb-4">
        <Lock className="w-6 h-6 brand-green" />
      </div>
      <p className="font-display text-2xl sm:text-3xl uppercase tracking-tight leading-tight">
        {isLifetime ? "Już oddałeś głos" : "Dzięki za głos!"}
      </p>
      <p className="text-sm text-black/55 mt-2 max-w-sm mx-auto">
        {isLifetime
          ? "W tym konkursie głosujemy tylko raz. Sprawdź jak idzie Twojemu wyborowi."
          : remaining
            ? `Wróć po ${remaining}, żeby zagłosować ponownie.`
            : "Wróć później, żeby zagłosować ponownie."}
      </p>
      {onShowResults && (
        <Button
          type="button"
          data-testid="locked-show-results-btn"
          onClick={onShowResults}
          className="btn-brand h-12 px-6 mt-5 text-sm font-semibold gap-2"
        >
          <BarChart3 className="w-4 h-4" /> Sprawdź wyniki
        </Button>
      )}
    </div>
  );
}

function ComboBoxPicker({
  options,
  selected,
  setSelected,
  optionLabel,
  hasPostalCodes,
  compact,
  query,
  setQuery,
  loading,
}) {
  const [open, setOpen] = useState(false);
  // Dynamic placeholder built from the campaign's `option_label`:
  //   • split on "/" and take the first segment so a label like
  //     "MIASTO / SKATEPARK" yields "miasto" (the thing the user actually
  //     types into the search field).
  //   • lower-case it — `option_label` is uppercased in the section header
  //     via CSS already, but inside a sentence it reads naturally lower-cased.
  //   • append " lub kod pocztowy" only when the active campaign actually has
  //     options with postal codes (flag comes from `/campaign/active`).
  const primaryToken = String(optionLabel || "opcję")
    .split("/")[0]
    .trim()
    .toLowerCase() || "opcję";
  const searchPlaceholder = `Wpisz ${primaryToken}${hasPostalCodes ? " lub kod pocztowy" : ""}…`;
  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          type="button"
          variant="outline"
          role="combobox"
          data-testid="city-combobox-trigger"
          style={{ backgroundColor: "#ffffff", color: "#000000" }}
          className="w-full justify-between bg-white border-black/10 text-black hover:bg-black/[0.02] hover:text-black hover:border-black/20 input-rounded h-14 px-5 text-base font-normal shadow-sm"
        >
          {selected ? (
            <span className="flex items-center gap-2.5 truncate text-left">
              <MapPin className="w-4 h-4 brand-green shrink-0" />
              <span className="font-semibold">{selected.city}</span>
              {selected.skatepark && (
                <span className="text-black/45 text-sm truncate hidden sm:inline">
                  — {selected.skatepark}
                </span>
              )}
            </span>
          ) : (
            <span className="text-black/40 font-normal">Wybierz {optionLabel}…</span>
          )}
          <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-40" />
        </Button>
      </PopoverTrigger>
      <PopoverContent
        className="w-[--radix-popover-trigger-width] p-0 bg-white border-black/10 input-rounded shadow-xl"
        align="start"
      >
        {/* shouldFilter=false → backend already filtered; cmdk won't try to
            score 18k postal codes on every keystroke (was the cause of the
            severe input lag). */}
        <Command className="bg-transparent" shouldFilter={false}>
          <CommandInput
            data-testid="city-combobox-input"
            placeholder={searchPlaceholder}
            value={query}
            onValueChange={setQuery}
            className="text-black"
          />
          <CommandList className={compact ? "max-h-52" : "max-h-72"}>
            <CommandEmpty className="py-6 text-center text-sm text-black/50">
              {loading ? "Szukam…" : "Nie znaleziono. Wpisz inną nazwę."}
            </CommandEmpty>
            <CommandGroup>
              {options.map((sp) => (
                <CommandItem
                  key={sp.id}
                  data-testid={`city-option-${sp.id}`}
                  value={sp.id}
                  onSelect={() => {
                    setSelected(sp);
                    setOpen(false);
                  }}
                  className="data-[selected=true]:bg-brand-green/10 data-[selected=true]:text-black text-black/90 cursor-pointer rounded-lg my-0.5"
                >
                  <Check
                    className={`mr-2 h-4 w-4 ${selected?.id === sp.id ? "opacity-100 brand-green" : "opacity-0"}`}
                  />
                  <div className="flex flex-col">
                    <span className="font-semibold">{sp.city}</span>
                    {(sp.skatepark || sp.region) && (
                      <span className="text-xs text-black/45">
                        {sp.skatepark}
                        {sp.skatepark && sp.region ? " · " : ""}
                        {sp.region}
                      </span>
                    )}
                  </div>
                </CommandItem>
              ))}
            </CommandGroup>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}

function RadioPicker({ options, selected, setSelected, compact }) {
  return (
    <div
      data-testid="radio-picker"
      className={`flex flex-col gap-1.5 ${compact ? "max-h-52" : "max-h-72"} overflow-y-auto pr-1 -mr-1`}
    >
      {options.map((sp) => {
        const active = selected?.id === sp.id;
        return (
          <button
            key={sp.id}
            type="button"
            data-testid={`radio-option-${sp.id}`}
            onClick={() => setSelected(sp)}
            className={`group w-full text-left flex items-center gap-3 px-4 py-3 rounded-xl border transition-all ${
              active
                ? "border-brand-green bg-brand-green/8 shadow-sm"
                : "border-black/8 bg-white hover:border-brand-green/40 hover:bg-brand-green/[0.03]"
            }`}
          >
            <span
              className={`relative w-5 h-5 rounded-full border-2 shrink-0 transition-colors ${
                active ? "border-brand-green" : "border-black/25 group-hover:border-brand-green/50"
              }`}
            >
              {active && (
                <span className="absolute inset-1 rounded-full bg-brand-green" />
              )}
            </span>
            <div className="min-w-0 flex-1">
              <p className={`font-semibold text-sm leading-tight truncate ${active ? "text-black" : "text-black/85"}`}>
                {sp.city}
              </p>
              {(sp.skatepark || sp.region) && (
                <p className="text-[11px] text-black/45 truncate mt-0.5">
                  {sp.skatepark}
                  {sp.skatepark && sp.region ? " · " : ""}
                  {sp.region}
                </p>
              )}
            </div>
          </button>
        );
      })}
    </div>
  );
}

export default function VoteForm({
  optionLabel = "miejsce",
  hasPostalCodes = false,
  displayMode = "combobox",
  onVoted,
  onShowResults,
  compact = false,
}) {
  const [options, setOptions] = useState([]);
  const [query, setQuery] = useState("");
  const [loadingOptions, setLoadingOptions] = useState(false);
  const [selected, setSelected] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [check, setCheck] = useState(null);
  const [checking, setChecking] = useState(true);

  const fingerprint = useMemo(() => computeFingerprint(), []);

  // Combobox: server-side search with debounce (huge postal_codes lists
  // otherwise lock up cmdk client-side filtering). Radio mode loads all.
  useEffect(() => {
    if (displayMode === "radio") {
      api
        .get("/skateparks?limit=2000")
        .then(({ data }) => setOptions(data))
        .catch((e) => toast.error(formatApiError(e)));
      return undefined;
    }
    let cancelled = false;
    setLoadingOptions(true);
    const q = query.trim();
    const t = setTimeout(
      () => {
        // No query → load full alphabetical list (still server-side, lightweight
        // projection — postal_codes excluded, so 1000+ rows ≈ 50 KB on the wire).
        // With query → top 50 by relevance score.
        const limit = q ? 50 : 2000;
        api
          .get(`/skateparks?limit=${limit}&q=${encodeURIComponent(q)}`)
          .then(({ data }) => {
            if (!cancelled) setOptions(data);
          })
          .catch(() => {})
          .finally(() => {
            if (!cancelled) setLoadingOptions(false);
          });
      },
      q ? 180 : 0
    );
    return () => {
      cancelled = true;
      clearTimeout(t);
    };
  }, [query, displayMode]);

  useEffect(() => {
    let cancelled = false;
    setChecking(true);
    api
      .get(`/vote/check?fingerprint=${encodeURIComponent(fingerprint)}`)
      .then(({ data }) => {
        if (!cancelled) setCheck(data);
      })
      .catch(() => {})
      .finally(() => {
        if (!cancelled) setChecking(false);
      });
    return () => {
      cancelled = true;
    };
  }, [fingerprint]);

  const handleSubmit = async (e) => {
    e?.preventDefault();
    if (!selected) {
      toast.error("Wybierz opcję z listy");
      return;
    }
    setSubmitting(true);
    try {
      const { data } = await api.post("/vote", {
        skatepark_id: selected.id,
        postal_code: "",
        fingerprint,
      });
      onVoted?.(selected, data.votes_for_skatepark);
      setSelected(null);
      setCheck({
        can_vote: false,
        mode: data.mode,
        days: data.days,
        next_allowed_at: data.next_allowed_at,
      });
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSubmitting(false);
    }
  };

  if (checking) {
    return (
      <div className="w-full py-6 text-center text-xs uppercase tracking-[0.3em] text-black/35">
        Ładowanie…
      </div>
    );
  }

  if (check && check.can_vote === false && check.reason !== "no_active_campaign") {
    return <LockedNotice check={check} onShowResults={onShowResults} />;
  }

  return (
    <form onSubmit={handleSubmit} data-testid="vote-form" className="w-full space-y-3">
      {displayMode === "radio" ? (
        <RadioPicker
          options={options}
          selected={selected}
          setSelected={setSelected}
          compact={compact}
        />
      ) : (
        <ComboBoxPicker
          options={options}
          selected={selected}
          setSelected={setSelected}
          optionLabel={optionLabel}
          hasPostalCodes={hasPostalCodes}
          compact={compact}
          query={query}
          setQuery={setQuery}
          loading={loadingOptions}
        />
      )}

      <Button
        type="submit"
        data-testid="submit-vote-btn"
        disabled={submitting || !selected}
        className="w-full btn-brand h-14 text-base font-semibold tracking-wide gap-2 group"
      >
        {submitting ? "Wysyłanie…" : "Zagłosuj"}
        <ArrowRight className="w-4 h-4 transition-transform group-hover:translate-x-0.5" />
      </Button>

      {formatLimitText(check) && (
        <p
          data-testid="vote-limit-info"
          className="text-center text-[10px] font-medium uppercase tracking-[0.25em] text-black/45"
        >
          <span className="brand-green">●</span> {formatLimitText(check)}
        </p>
      )}

      {onShowResults && (
        <div className="flex justify-center pt-1">
          <button
            type="button"
            data-testid="check-results-link"
            onClick={onShowResults}
            className="inline-flex items-center gap-1.5 text-sm font-semibold text-brand-green hover:text-black transition-colors border-b border-brand-green/40 hover:border-black pb-0.5"
          >
            Sprawdź wyniki <ArrowRight className="w-3.5 h-3.5" />
          </button>
        </div>
      )}
    </form>
  );
}
